import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './TestScreen.css';

const floatingElements = ['∑', 'π', '⚛', '🧪', '🧬', 'A', '📜'];

// Stable random positions so they don't re-randomize on every render
const floatingPositions = floatingElements.map(() => ({
    left: `${Math.random() * 90}%`,
    top: `${Math.random() * 90}%`,
    delay: `${Math.random() * 10}s`,
    size: `${1 + Math.random() * 2}rem`
}));

const TestScreen = () => {
    const { t } = useTranslation(['test', 'common']);
    const navigate = useNavigate();

    const location = useLocation();
    const meta = location.state || {};

    // Real questions from TestLoader; fallback to a demo question if missing
    const questions = useMemo(() => {
        if (Array.isArray(meta.questions) && meta.questions.length > 0) return meta.questions;
        return [{
            id: 1,
            question: "What is the capital of India?",
            options: { A: "Mumbai", B: "Kolkata", C: "New Delhi", D: "Chennai" },
            correct_answer: "C",
            explanation: "New Delhi is the capital of India.",
            difficulty: "easy"
        }];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const subject = meta.subject || 'Science';
    const cls = meta.class || '10';
    const subjectId = meta.subject_id || 1;
    const chapter = meta.chapter || 'Chapter 1';
    const board = meta.board || 'CBSE';
    const userId = parseInt(localStorage.getItem('userId') || '0', 10) || null;

    const totalQuestions = questions.length;

    const [currentQuestion, setCurrentQuestion] = useState(1);
    const [selectedOption, setSelectedOption] = useState(null);
    const [answers, setAnswers] = useState({}); // { questionIndex: 'A'|'B'|'C'|'D' }
    const [timeLeft, setTimeLeft] = useState(totalQuestions * 90); // 90s per question

    // Timer
    useEffect(() => {
        if (timeLeft <= 0) {
            handleSubmitTest();
            return;
        }
        const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [timeLeft]); // eslint-disable-line react-hooks/exhaustive-deps

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Index is 0-based for array access
    const qIndex = currentQuestion - 1;
    const currentQ = questions[qIndex] || questions[0];

    // Options can be an object {A,B,C,D} or an array [{id,text}]
    const optionList = useMemo(() => {
        if (!currentQ) return [];
        if (Array.isArray(currentQ.options)) return currentQ.options;
        return Object.entries(currentQ.options).map(([id, text]) => ({ id, text }));
    }, [currentQ]);

    const handleSelect = (optionId) => {
        setSelectedOption(optionId);
        setAnswers(prev => ({ ...prev, [qIndex]: optionId }));
    };

    const goToQuestion = (idx) => {
        setCurrentQuestion(idx + 1);
        setSelectedOption(answers[idx] || null);
    };

    const goNext = () => {
        if (currentQuestion < totalQuestions) {
            const nextIdx = currentQuestion; // currentQuestion is 1-based, so nextIdx = qIndex+1
            setCurrentQuestion(currentQuestion + 1);
            setSelectedOption(answers[nextIdx] || null);
        } else {
            handleSubmitTest();
        }
    };

    const goPrev = () => {
        if (currentQuestion > 1) {
            const prevIdx = qIndex - 1;
            setCurrentQuestion(currentQuestion - 1);
            setSelectedOption(answers[prevIdx] || null);
        }
    };

    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmitTest = async () => {
        if (hasSubmitted) return;
        setHasSubmitted(true);
        setSubmitting(true);

        const correctCount = questions.reduce((acc, q, i) => {
            return acc + (answers[i] === q.correct_answer ? 1 : 0);
        }, 0);
        const scorePercent = Math.round((correctCount / totalQuestions) * 100);

        // Build common state for navigation
        const baseState = {
            questions,
            answers,
            board,
            class: cls,
            subject,
            subject_id: subjectId,
            chapter,
            chapter_id: meta.chapter_id || null,
            user_id: userId,
            score_percentage: scorePercent,
            correct_answers: correctCount,
            total_questions: totalQuestions,
        };

        // Submit to backend FIRST so we get a real test_id before navigating
        try {
            const res = await fetch(`${API_BASE_URL}submit_test.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    user_id: userId,
                    subject_id: subjectId,
                    chapter_id: meta.chapter_id || null,
                    score_percentage: scorePercent,
                    total_questions: totalQuestions,
                    correct_answers: correctCount,
                })
            });
            const result = await res.json();

            // Navigate with the real test_id so generate_analysis.php can save to DB
            navigate('/ai-doctor-report', {
                state: { ...baseState, test_id: result.test_id || null }
            });
        } catch (err) {
            console.error('Submission failed:', err);
            // Navigate anyway — analysis still works, just won't be saved to DB
            navigate('/ai-doctor-report', {
                state: { ...baseState, test_id: null }
            });
        }
    };


    return (
        <div className="test-screen-container">
            {/* Background Decor */}
            <div className="test-background-overlay">
                {floatingElements.map((sym, i) => (
                    <div
                        key={i}
                        className="floating-symbol"
                        style={{
                            left: floatingPositions[i].left,
                            top: floatingPositions[i].top,
                            animationDelay: floatingPositions[i].delay,
                            fontSize: floatingPositions[i].size
                        }}
                    >
                        {sym}
                    </div>
                ))}
            </div>

            {/* Top Bar */}
            <header className="top-bar">
                <div className="app-logo-box">
                    <div className="test-title">
                        {t('test:test.diagnosticTitle', { board, subject, cls, defaultValue: `${board} ${subject} Class ${cls} – SkillTherapy AI Diagnostic` })}
                    </div>
                </div>


                <div className="timer-box">
                    <span className="timer-icon">{t('test:test.timer', '⏱')}</span>

                    <span className="timer-text" style={{ color: timeLeft < 60 ? '#ff6b6b' : undefined }}>
                        {formatTime(timeLeft)}
                    </span>
                </div>
            </header>

            <main className="main-content">
                {/* Side Panel */}
                <aside className="side-panel">
                    <div className="category-card">
                        <h3 style={{ fontSize: '1rem', marginBottom: '1.5rem', opacity: 0.6 }}>{t('test:test.subjectHeader', 'SUBJECT')}</h3>

                        <nav className="subject-nav">
                            <div className="nav-item active">
                                <span className="item-icon">📚</span>
                                <span className="item-name">{subject}</span>
                            </div>

                        </nav>
                    </div>

                    <div className="category-card" style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '1rem', marginBottom: '1.5rem', opacity: 0.6 }}>{t('test:test.questionNav', 'QUESTION NAV')}</h3>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
                            {questions.map((_, i) => {
                                const isActive = i === qIndex;
                                const isAnswered = answers[i] !== undefined;
                                return (
                                    <div
                                        key={i}
                                        onClick={() => goToQuestion(i)}
                                        style={{
                                            aspectRatio: '1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '8px',
                                            background: isActive
                                                ? 'rgba(0, 242, 255, 0.2)'
                                                : isAnswered
                                                    ? 'rgba(0, 255, 150, 0.12)'
                                                    : 'rgba(255, 255, 255, 0.05)',
                                            border: isActive
                                                ? '1px solid var(--neon-blue)'
                                                : isAnswered
                                                    ? '1px solid rgba(0,255,150,0.4)'
                                                    : '1px solid var(--glass-border)',
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            fontWeight: isActive ? 700 : 400,
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        {i + 1}
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ marginTop: '0.75rem', fontSize: '0.7rem', opacity: 0.5, display: 'flex', gap: '0.75rem' }}>
                            <span>🟦 {t('test:test.legendCurrent', 'Current')}</span>
                            <span>🟩 {t('test:test.legendAnswered', 'Answered')}</span>
                        </div>

                    </div>
                </aside>

                {/* Question Area */}
                <section className="question-area">
                    <div className="question-card">
                        <span className="question-number">
                            {t('test:test.questionCount', { current: currentQuestion, total: totalQuestions, defaultValue: `Question ${currentQuestion} / ${totalQuestions}` })}

                            {currentQ.difficulty && (
                                <span style={{
                                    marginLeft: '1rem',
                                    fontSize: '0.7rem',
                                    padding: '2px 8px',
                                    borderRadius: '20px',
                                    background: currentQ.difficulty === 'easy'
                                        ? 'rgba(0,255,150,0.15)' : currentQ.difficulty === 'hard'
                                            ? 'rgba(255,80,80,0.15)' : 'rgba(255,200,0,0.15)',
                                    color: currentQ.difficulty === 'easy'
                                        ? '#00ff96' : currentQ.difficulty === 'hard'
                                            ? '#ff6b6b' : '#ffd700',
                                    textTransform: 'capitalize'
                                }}>
                                    {t(`test:test.difficulty.${currentQ.difficulty}`, currentQ.difficulty)}
                                </span>

                            )}
                        </span>
                        <h2 className="question-text">{currentQ.question}</h2>
                    </div>

                    <div className="options-grid">
                        {optionList.map((option) => (
                            <div
                                key={option.id}
                                className={`option-card ${selectedOption === option.id ? 'selected' : ''}`}
                                onClick={() => handleSelect(option.id)}
                            >
                                <div className="option-label">{option.id}</div>
                                <div className="option-content">{option.text}</div>
                            </div>
                        ))}
                    </div>
                </section>
            </main>

            {/* Footer Navigation */}
            <footer className="footer-nav">
                <div className="progress-container">
                    <div className="progress-info">
                        {t('test:test.footerInfo', { 
                            current: currentQuestion, 
                            total: totalQuestions, 
                            answered: Object.keys(answers).length,
                            defaultValue: `Question ${currentQuestion} of ${totalQuestions} · ${Object.keys(answers).length} answered`
                        })}
                    </div>

                    <div className="progress-bar-wrap">
                        <div className="progress-inner" style={{ width: `${(currentQuestion / totalQuestions) * 100}%` }}></div>
                    </div>
                </div>

                <div className="nav-buttons">
                    <button className="btn-secondary" onClick={goPrev} disabled={currentQuestion === 1}>
                        {t('test:test.previous', 'Previous')}
                    </button>

                    <button
                        className="btn-primary"
                        onClick={goNext}
                        disabled={submitting}
                        style={submitting ? { opacity: 0.8, cursor: 'not-allowed' } : {}}
                    >
                        {submitting ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span className="submit-spinner"></span>
                                {t('test:test.submitting', 'Submitting...')}
                            </span>
                        ) : (
                            currentQuestion === totalQuestions ? t('test:test.submitTest', 'Submit Test') : t('test:test.nextQuestion', 'Next Question')
                        )}

                    </button>
                </div>
            </footer>
        </div>
    );
};

export default TestScreen;
